import type { ErrorEvent } from '@sentry/vue';

// Messages Sentry drops at init (`ignoreErrors`). Each entry is browser-extension
// or in-app-browser chatter with no app frames, so there is no call site to
// guard. Every pattern is anchored so it can only match its exact signature.
//
// Deliberately NOT extended to the browser fetch-failure messages ("Failed to
// fetch" / "Load failed" / "NetworkError…"): globally ignoring those would also
// hide a genuine API-origin/CORS regression on this auth app. Those are filtered
// at their call sites instead, where the surrounding context proves they're
// bootstrap network blips.
export const sentryIgnoreErrors: RegExp[] = [
  // An extension messaging a closed tab, surfacing as an unhandled rejection
  // (DABBLE-ACCOUNTS-2-1A).
  /^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/,
  // Apple Mail's link-preview script, injected as global code into a page
  // opened from an email, reads the page's `og:image` meta tag, which this
  // app doesn't have (DABBLE-ACCOUNTS-2-4S). No first-party code reads it.
  /^null is not an object \(evaluating 'document\.querySelector\('meta\[property="og:image"\]'\)\.content'\)$/,
  // Facebook's iOS in-app browser injects global code that reads its own
  // `window._META` bootstrap object before defining it (DABBLE-ACCOUNTS-2-4X).
  // No first-party code has a `_META`.
  /^undefined is not an object \(evaluating 'window\._META\.IABInnerFrame'\)$/,
  // A writing-assistant extension's content script asks its background page for
  // its dictionaries, voices and settings, and when the page doesn't answer it
  // rejects every pending call with a bare string, "Request timeout <call>". One
  // signup produced 25 issues (DABBLE-ACCOUNTS-2-52…5T). No first-party code makes
  // these calls or rejects with a string, so only a non-Error string rejection
  // naming one of the extension's call shapes matches.
  /^Non-Error promise rejection captured with value: Request timeout (?:[\w-]+Distributor\.getValue|DefineExpirationForLanguagePacks\.getValue|is(?:Dictate|MathOcr|Prediction)Available|getDictionariesByLanguageId)$/,
];

// Stack-frame URLs Sentry drops at init (`denyUrls`): code a browser extension runs
// against the page. An error thrown inside one is never ours. `denyUrls` judges the
// innermost frame with a URL, so an extension that merely triggers a first-party throw
// still reports. Mirrors dabble-writer-3.0's list.
export const sentryDenyUrls: RegExp[] = [
  /-extension:\/\//,
  // Safari app extensions report a macOS bundle path rather than a URL scheme: PayPal
  // Honey rejecting a promise in its own bundle on the profile page (DABBLE-ACCOUNTS-2-4W).
  /\.safariextension\//,
  /\/Extension\.appex\//,
];

const FETCH_FAILURE = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/;

/**
 * True when a fetch failed from a script that runs inside the page document itself.
 *
 * Adware and some extensions inject an inline `<script>` into the page. The browser
 * attributes that script's frames to the page URL, as if it were one of ours, so
 * `denyUrls` can't see it. DABBLE-ACCOUNTS-2-4V was one: an `<img>` onload handler on
 * `/billing/portal:62` (a line and column past the end of our `index.html`) POSTing to a
 * third-party host. This app's only inline script (the URL hand-off in `index.html`)
 * never calls `fetch`, so a fetch failure with a frame on the page document can't be
 * ours. Fetch failures from the bundle under `/assets/` keep reporting, and the message
 * alone never decides it (see the note at the top of this file).
 */
export function isInjectedPageFetchFailure(event: ErrorEvent): boolean {
  const thrown = event.exception?.values?.at(-1);
  if (!FETCH_FAILURE.test(thrown?.value ?? '')) return false;
  const page = documentPath(event.request?.url);
  if (!page) return false;
  return (thrown?.stacktrace?.frames ?? []).some((frame) => documentPath(frame.filename || frame.abs_path) === page);
}

/** Sentry's `beforeSend`: drops the injected-script noise above, passes everything else. */
export function sentryBeforeSend(event: ErrorEvent): ErrorEvent | null {
  return isInjectedPageFetchFailure(event) ? null : event;
}

/** `origin + pathname` of an absolute http(s) URL, or undefined for anything else. */
function documentPath(url: string | undefined): string | undefined {
  if (!url || !/^https?:\/\//.test(url)) return undefined;
  try {
    const { origin, pathname } = new URL(url);
    return origin + pathname;
  } catch {
    return undefined;
  }
}
